import {
  useAllCategoriesQuery,
  useAllCategoryGroupsQuery,
} from '@transactions/graphql';
import * as React from 'react';
import {FilterDropdown} from 'src/components/FilterDropdown';

type Props = {
  categories: string[] | null;
  setCategories: (categories: string[] | null) => void;
};

/**
 * Multi-select category filter pill (FilterDropdown in `multiple` mode, with
 * type-ahead), styled like the other filter pills. Options render under the
 * user's category-group sections — groups in display order (members in
 * member order), then ungrouped categories in name order — and each group
 * header toggles its whole group. The external value stays
 * `string[] | null` with null meaning no filter.
 */
export const CategoryFilterInput = (props: Props) => {
  const {data} = useAllCategoriesQuery();
  const {data: groupsData} = useAllCategoryGroupsQuery();
  const allCategories = React.useMemo(
    () => data?.allCategories ?? [],
    [data],
  );
  const groups = React.useMemo(
    () => groupsData?.allCategoryGroups ?? [],
    [groupsData],
  );

  // Grouped browse order, same shape as CategoryPicker's: member ids that
  // don't resolve are dropped, and a category is in at most one group.
  const options = React.useMemo(() => {
    const byId = new Map(allCategories.map((category) => [category._id, category]));
    const grouped = new Set(groups.flatMap((group) => group.categoryIds));
    return [
      ...groups.flatMap((group) =>
        group.categoryIds.flatMap((id) => {
          const category = byId.get(id);
          return category
            ? [
                {
                  key: category._id,
                  label: category.name,
                  group: group.name,
                  groupKey: group._id,
                },
              ]
            : [];
        }),
      ),
      ...allCategories
        .filter((category) => !grouped.has(category._id))
        .map((category) => ({key: category._id, label: category.name})),
    ];
  }, [allCategories, groups]);

  // Drop ids that no longer exist (e.g. a deleted category persisted in
  // localStorage) — the previous implementation derived its selection the
  // same way.
  const knownIds = React.useMemo(
    () => new Set(allCategories.map((category) => category._id)),
    [allCategories],
  );
  const value = (props.categories ?? []).filter((id) => knownIds.has(id));

  return (
    <FilterDropdown
      multiple
      searchable
      label="Categories"
      allLabel="All categories"
      options={options}
      value={value}
      onChange={(ids) => props.setCategories(ids.length > 0 ? ids : null)}
    />
  );
};
